import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { bookingApi, PublicLinkInfo, AvailabilityDay, AvailabilitySlot } from '../services/api';
import { CalendlyStyleEmbed } from '../components/CalendlyStyleEmbed';
import { CalendlyCustomFormFields } from '../components/CustomFormFields';
import { useEmbedMode } from '../hooks/useEmbedMode';
import { useTheme } from '../hooks/useTheme';
import {
    Calendar,
    Clock,
    User,
    Mail,
    MessageSquare,
    Star,
    X,
    Check,
    ChevronRight,
    ChevronLeft,
    Sun,
    Moon,
    Globe,
    ArrowLeft
} from 'lucide-react';
import { Footer } from '../components/Footer';
import { detectTimezone, TIMEZONES, getTimezoneLabel } from '../utils/timezones';

// Constants for calendar
const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Helper function to format date as "May 1st, 2026"
const formatDateReadable = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    const dayNum = date.getDate();
    const yearNum = date.getFullYear();

    // Add ordinal suffix (1st, 2nd, 3rd, etc.)
    const suffix = (d: number) => {
        if (d > 3 && d < 21) return 'th';
        switch (d % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    return `${monthName} ${dayNum}${suffix(dayNum)}, ${yearNum}`;
};

// Format date for display in header
const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

interface SelectedSlot extends AvailabilitySlot {
    date: string;
    day_name: string;
}

type Step = 'select' | 'form' | 'success';

export function PublicBookingPage() {
    const { slug } = useParams<{ slug: string }>();
    const { isEmbedded, notifyParentHeight } = useEmbedMode();

    // State
    const [link, setLink] = useState<PublicLinkInfo | null>(null);

    // Theme management - initialized after link loads
    const { toggleTheme, isDark } = useTheme({
        defaultTheme: link?.default_theme || 'dark',
        storageKey: slug
    });
    const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
    const [timezone, setTimezone] = useState(() => detectTimezone());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Step state
    const [step, setStep] = useState<Step>('select');

    // Date/time selection state
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);

    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [customFields, setCustomFields] = useState<Record<string, any>>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Calendar view month
    const initialMonth = useMemo(() => {
        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
            }
            const firstDate = new Date(availability[0].date + 'T00:00:00');
            return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
        }
        const today = new Date();
        return { year: today.getFullYear(), month: today.getMonth() };
    }, [availability]);

    const [viewMonth, setViewMonth] = useState(initialMonth);

    // Notify parent of height changes (for embed mode)
    useEffect(() => {
        notifyParentHeight();
    }, [step, selectedSlots.length, isLoading, notifyParentHeight]);

    // Reset state when timezone/availability changes
    useEffect(() => {
        setSelectedDate(null);
        setSelectedSlots([]);
        setStep('select');

        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                setViewMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
            } else {
                const firstDate = new Date(availability[0].date + 'T00:00:00');
                setViewMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
            }
        }
    }, [timezone, availability]);

    // Load booking link data
    useEffect(() => {
        if (!slug) return;
        loadLink();
    }, [slug, timezone]);

    // Update document title with event name
    useEffect(() => {
        if (link?.name) {
            document.title = `AAM Let's Meet: ${link.name}`;
        }
        return () => {
            document.title = "AAM Let's Meet";
        };
    }, [link?.name]);

    const loadLink = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await bookingApi.getLink(slug!, timezone);
            setLink(data.link);
            setAvailability(data.availability);
        } catch (err: any) {
            setError(err.message || 'Failed to load booking page');
        } finally {
            setIsLoading(false);
        }
    };

    // Build set of available dates
    const availableDatesSet = useMemo(() => {
        return new Set(availability.filter(d => d.slots.length > 0).map(d => d.date));
    }, [availability]);

    // Get date range for navigation limits
    const dateRange = useMemo(() => {
        if (availability.length === 0) return { min: null, max: null };
        const dates = availability.map(d => d.date).sort();
        return { min: dates[0], max: dates[dates.length - 1] };
    }, [availability]);

    // Generate calendar grid
    const calendarDays = useMemo(() => {
        const { year, month } = viewMonth;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startDayOfWeek = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();

        const days: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean }[] = [];

        // Previous month's trailing days
        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const prevYear = month === 0 ? year - 1 : year;
            const prevMonthNum = month === 0 ? 11 : month - 1;
            const dateStr = `${prevYear}-${String(prevMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        // Current month's days
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: true, isToday: dateStr === todayStr });
        }

        // Next month's leading days (fill to 6 rows)
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const nextYear = month === 11 ? year + 1 : year;
            const nextMonthNum = month === 11 ? 0 : month + 1;
            const dateStr = `${nextYear}-${String(nextMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        return days;
    }, [viewMonth]);

    // Get slots for selected date
    const slotsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        const day = availability.find(d => d.date === selectedDate);
        return day?.slots || [];
    }, [selectedDate, availability]);

    // Get day info for selected date
    const selectedDayInfo = useMemo(() => {
        if (!selectedDate) return null;
        return availability.find(d => d.date === selectedDate);
    }, [selectedDate, availability]);

    // Navigation helpers
    const canGoPrev = dateRange.min ? (() => {
        const minDate = new Date(dateRange.min + 'T00:00:00');
        return viewMonth.year > minDate.getFullYear() ||
            (viewMonth.year === minDate.getFullYear() && viewMonth.month > minDate.getMonth());
    })() : false;

    const canGoNext = dateRange.max ? (() => {
        const maxDate = new Date(dateRange.max + 'T00:00:00');
        return viewMonth.year < maxDate.getFullYear() ||
            (viewMonth.year === maxDate.getFullYear() && viewMonth.month < maxDate.getMonth());
    })() : false;

    const goToPrevMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    };

    const goToNextMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    };

    const handleTimezoneChange = (newTz: string) => {
        setTimezone(newTz);
        setSelectedSlots([]);
        setSelectedDate(null);
    };

    // Handle date selection
    const handleDateSelect = (date: string) => {
        setSelectedDate(date);
    };

    // Handle time selection
    const handleTimeSelect = (slot: AvailabilitySlot) => {
        if (!selectedDayInfo || !selectedDate) return;

        const slotWithDate: SelectedSlot = {
            ...slot,
            date: selectedDate,
            day_name: selectedDayInfo.day_name
        };

        if (isDirectBooking) {
            // Direct booking: single selection
            setSelectedSlots([slotWithDate]);
        } else {
            // Proposal booking: toggle selection
            const exists = selectedSlots.some(s => s.start_time_utc === slot.start_time_utc);
            if (exists) {
                setSelectedSlots(prev => prev.filter(s => s.start_time_utc !== slot.start_time_utc));
            } else {
                if (link && selectedSlots.length >= link.max_proposals) {
                    alert(`You can only select up to ${link.max_proposals} time preferences`);
                    return;
                }
                setSelectedSlots(prev => [...prev, slotWithDate]);
            }
        }
    };

    // Confirm time and go to form
    const handleConfirmTime = () => {
        if (selectedSlots.length > 0) {
            setStep('form');
        }
    };

    // Go back to time selection
    const handleBackToSelect = () => {
        setStep('select');
    };

    // Remove a selected slot
    const removeSlot = (index: number) => {
        setSelectedSlots(prev => prev.filter((_, i) => i !== index));
    };

    // Move slot order (for proposals)
    const moveSlot = (from: number, to: number) => {
        if (to < 0 || to >= selectedSlots.length) return;
        setSelectedSlots(prev => {
            const result = [...prev];
            const [removed] = result.splice(from, 1);
            result.splice(to, 0, removed);
            return result;
        });
    };

    const isDirectBooking = link?.booking_type === 'direct';

    // Submit booking
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedSlots.length === 0 || !name.trim() || !email.trim()) return;

        setIsSubmitting(true);
        setCustomFieldErrors({});

        try {
            const customFieldsData = Object.keys(customFields).length > 0 ? customFields : undefined;

            if (isDirectBooking) {
                await bookingApi.directBook(slug!, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    custom_fields: customFieldsData,
                    slot: {
                        start_time_utc: selectedSlots[0].start_time_utc,
                        end_time_utc: selectedSlots[0].end_time_utc
                    },
                    timezone
                });
            } else {
                await bookingApi.submit(slug!, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    custom_fields: customFieldsData,
                    slots: selectedSlots.map(s => ({
                        start_time_utc: s.start_time_utc,
                        end_time_utc: s.end_time_utc
                    })),
                    timezone
                });
            }

            setStep('success');
        } catch (err: any) {
            if (err.errors && typeof err.errors === 'object') {
                setCustomFieldErrors(err.errors);
            }
            alert(err.message || 'Failed to submit booking');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get current time display for timezone selector
    const getCurrentTimeDisplay = () => {
        try {
            const now = new Date();
            const timeStr = now.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: timezone
            });
            return timeStr.toLowerCase();
        } catch {
            return '';
        }
    };

    // Loading state
    if (isLoading) {
        if (isEmbedded) {
            return (
                <div className="calendly-embed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="calendly-loading">
                        <div className="calendly-loading-spinner" />
                        <span>Loading...</span>
                    </div>
                </div>
            );
        }
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="spinner" />
                </div>
                <Footer />
            </div>
        );
    }

    // Error state
    if (error || !link) {
        if (isEmbedded) {
            return (
                <div className="calendly-embed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="calendly-error-state">
                        <Calendar size={48} />
                        <h2>Booking Unavailable</h2>
                        <p>{error || 'This booking link is no longer active.'}</p>
                    </div>
                </div>
            );
        }
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-5)'
                }}>
                    <div className="calendly-embed" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <Calendar size={48} style={{ color: isDark ? '#666' : '#94a3b8', margin: '0 auto var(--space-4)' }} />
                        <h2 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
                            Booking Unavailable
                        </h2>
                        <p style={{ color: isDark ? '#888' : '#64748b' }}>
                            {error || 'This booking link is no longer active.'}
                        </p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Render Calendly-style embed when in embed mode
    if (isEmbedded && slug) {
        return (
            <CalendlyStyleEmbed
                link={link}
                availability={availability}
                timezone={timezone}
                onTimezoneChange={handleTimezoneChange}
                slug={slug}
            />
        );
    }

    // Success screen
    if (step === 'success') {
        return (
            <div className="booking-page calendly-full-page">
                {/* Header */}
                <div className="calendly-header">
                    <div className="calendly-header-content">
                        <div className="calendly-header-logo">
                            <Calendar size={24} />
                            <span>AAM <span className="calendly-header-accent">Let's Meet</span></span>
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>

                {/* Success Content */}
                <div className="calendly-page-content">
                    <div className="calendly-success">
                        <div className="calendly-success-icon">
                            <Check size={48} />
                        </div>
                        <h2>{isDirectBooking ? 'You are scheduled' : 'Request submitted'}</h2>
                        <p>
                            {isDirectBooking
                                ? `A calendar invitation has been sent to ${email}`
                                : `Your request has been sent to ${link.user_name}. You'll receive an email when they confirm.`
                            }
                        </p>
                        <div className="calendly-success-details">
                            <div className="calendly-success-detail">
                                <strong>{link.name}</strong>
                            </div>
                            {selectedSlots.map((slot, index) => (
                                <div key={slot.start_time_utc} className="calendly-success-detail">
                                    {!isDirectBooking && <span className="calendly-success-rank">#{index + 1}</span>}
                                    <Clock size={16} />
                                    <span>{formatDateDisplay(slot.date)}</span>
                                    <span>{slot.start_time_local} - {slot.end_time_local}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    // Form screen
    if (step === 'form') {
        return (
            <div className="booking-page calendly-full-page">
                {/* Header */}
                <div className="calendly-header">
                    <div className="calendly-header-content">
                        <div className="calendly-header-logo">
                            <Calendar size={24} />
                            <span>AAM <span className="calendly-header-accent">Let's Meet</span></span>
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="calendly-page-content">
                    <div className="calendly-embed calendly-embed-fullpage">
                        {/* Left Info Panel */}
                        <div className="calendly-info-panel">
                            {link.logo_url && (
                                <img src={link.logo_url} alt="" className="calendly-logo" />
                            )}
                            <div className="calendly-user-name">{link.user_name}</div>
                            <h1 className="calendly-event-title">{link.name}</h1>
                            <div className="calendly-event-meta">
                                <Clock size={16} />
                                <span>{link.duration_minutes} min</span>
                            </div>
                            {link.description && (
                                <p className="calendly-event-description">{link.description}</p>
                            )}

                            <div className="calendly-selected-time">
                                <div className="calendly-selected-time-header">
                                    {isDirectBooking ? 'Selected Time' : `Selected Times (${selectedSlots.length})`}
                                </div>
                                {selectedSlots.map((slot, index) => (
                                    <div key={slot.start_time_utc} className="calendly-selected-time-item">
                                        {!isDirectBooking && (
                                            <span className="calendly-selected-time-rank">
                                                {index === 0 && <Star size={12} />}
                                                #{index + 1}
                                            </span>
                                        )}
                                        <div className="calendly-selected-time-date">
                                            {slot.day_name}, {formatDateReadable(slot.date)}
                                        </div>
                                        <div className="calendly-selected-time-slot">
                                            {slot.start_time_local} - {slot.end_time_local}
                                        </div>
                                    </div>
                                ))}
                                <div className="calendly-selected-time-tz">
                                    {getTimezoneLabel(timezone)}
                                </div>
                            </div>
                        </div>

                        {/* Right Form Panel */}
                        <div className="calendly-form-panel">
                            <button className="calendly-back-btn" onClick={handleBackToSelect}>
                                <ArrowLeft size={16} />
                                Back
                            </button>

                            <h2 className="calendly-form-title">Enter Details</h2>

                            <form onSubmit={handleSubmit} className="calendly-form">
                                <div className="calendly-form-group">
                                    <label className="calendly-label">Name *</label>
                                    <div className="calendly-input-wrapper">
                                        <User size={18} />
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Your name"
                                            required
                                            className="calendly-input"
                                        />
                                    </div>
                                </div>

                                <div className="calendly-form-group">
                                    <label className="calendly-label">Email *</label>
                                    <div className="calendly-input-wrapper">
                                        <Mail size={18} />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="your@email.com"
                                            required
                                            className="calendly-input"
                                        />
                                    </div>
                                </div>

                                <div className="calendly-form-group">
                                    <label className="calendly-label">
                                        Please share anything that will help prepare for our meeting.
                                    </label>
                                    <div className="calendly-input-wrapper textarea">
                                        <MessageSquare size={18} />
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Optional message..."
                                            rows={3}
                                            className="calendly-textarea"
                                        />
                                    </div>
                                </div>

                                {/* Custom form fields */}
                                {link.custom_fields && link.custom_fields.length > 0 && (
                                    <CalendlyCustomFormFields
                                        fields={link.custom_fields}
                                        values={customFields}
                                        onChange={(fieldName, value) => {
                                            setCustomFields(prev => ({ ...prev, [fieldName]: value }));
                                            if (customFieldErrors[fieldName]) {
                                                setCustomFieldErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors[fieldName];
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        errors={customFieldErrors}
                                    />
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || !name.trim() || !email.trim()}
                                    className="calendly-submit-btn"
                                >
                                    {isSubmitting ? 'Scheduling...' : (isDirectBooking ? 'Schedule Event' : 'Request Meeting')}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    // Main select screen
    return (
        <div className="booking-page calendly-full-page">
            {/* Header */}
            <div className="calendly-header">
                <div className="calendly-header-content">
                    <div className="calendly-header-logo">
                        <Calendar size={24} />
                        <span>AAM <span className="calendly-header-accent">Let's Meet</span></span>
                    </div>
                    <button
                        onClick={toggleTheme}
                        className="theme-toggle"
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="calendly-page-content">
                <div className="calendly-embed calendly-embed-fullpage">
                    {/* Left Info Panel */}
                    <div className="calendly-info-panel">
                        {link.logo_url && (
                            <img src={link.logo_url} alt="" className="calendly-logo" />
                        )}
                        <div className="calendly-user-name">{link.user_name}</div>
                        <h1 className="calendly-event-title">{link.name}</h1>
                        <div className="calendly-event-meta">
                            <Clock size={16} />
                            <span>{link.duration_minutes} min</span>
                        </div>
                        {!isDirectBooking && (
                            <div className="calendly-event-meta">
                                <Star size={16} />
                                <span>Select up to {link.max_proposals} time{link.max_proposals > 1 ? 's' : ''}</span>
                            </div>
                        )}
                        {link.description && (
                            <p className="calendly-event-description">{link.description}</p>
                        )}

                        {/* Selected times summary for proposal mode */}
                        {!isDirectBooking && selectedSlots.length > 0 && (
                            <div className="calendly-selected-times-summary">
                                <div className="calendly-selected-times-header">
                                    <Star size={14} />
                                    Your Preferences ({selectedSlots.length}/{link.max_proposals})
                                </div>
                                {selectedSlots.map((slot, index) => (
                                    <div key={slot.start_time_utc} className="calendly-selected-time-row">
                                        <div className="calendly-selected-time-info">
                                            <span className="calendly-selected-time-rank">#{index + 1}</span>
                                            <div>
                                                <div className="calendly-selected-time-date-sm">
                                                    {slot.day_name} {formatDateReadable(slot.date)}
                                                </div>
                                                <div className="calendly-selected-time-time">
                                                    {slot.start_time_local}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="calendly-selected-time-actions">
                                            {selectedSlots.length > 1 && (
                                                <>
                                                    <button
                                                        onClick={() => moveSlot(index, index - 1)}
                                                        disabled={index === 0}
                                                        className="calendly-move-btn"
                                                        title="Move up"
                                                    >
                                                        <ChevronLeft size={14} style={{ transform: 'rotate(90deg)' }} />
                                                    </button>
                                                    <button
                                                        onClick={() => moveSlot(index, index + 1)}
                                                        disabled={index === selectedSlots.length - 1}
                                                        className="calendly-move-btn"
                                                        title="Move down"
                                                    >
                                                        <ChevronLeft size={14} style={{ transform: 'rotate(-90deg)' }} />
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => removeSlot(index)}
                                                className="calendly-remove-btn"
                                                title="Remove"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    onClick={handleConfirmTime}
                                    className="calendly-next-btn"
                                >
                                    Next
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right Booking Panel */}
                    <div className="calendly-booking-panel">
                        <div className={`calendly-calendar-section ${selectedDate ? 'has-times' : ''}`}>
                            <h2 className="calendly-section-title">Select a Date & Time</h2>

                            {/* Calendar Header */}
                            <div className="calendly-calendar-header">
                                <button
                                    onClick={goToPrevMonth}
                                    disabled={!canGoPrev}
                                    className="calendly-nav-btn"
                                    aria-label="Previous month"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="calendly-month-title">
                                    {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
                                </span>
                                <button
                                    onClick={goToNextMonth}
                                    disabled={!canGoNext}
                                    className="calendly-nav-btn"
                                    aria-label="Next month"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>

                            {/* Weekday Headers */}
                            <div className="calendly-weekdays">
                                {DAYS_OF_WEEK.map((day, i) => (
                                    <div key={i} className="calendly-weekday">{day}</div>
                                ))}
                            </div>

                            {/* Calendar Grid */}
                            <div className="calendly-calendar-grid">
                                {calendarDays.map((day, i) => {
                                    const isAvailable = availableDatesSet.has(day.date);
                                    const isSelected = day.date === selectedDate;
                                    const isPast = day.date < new Date().toISOString().split('T')[0];
                                    const isDisabled = !isAvailable || !day.isCurrentMonth || isPast;

                                    return (
                                        <button
                                            key={i}
                                            onClick={() => isAvailable && day.isCurrentMonth && !isPast && handleDateSelect(day.date)}
                                            disabled={isDisabled}
                                            className={`calendly-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${isAvailable && !isPast ? 'available' : ''} ${isSelected ? 'selected' : ''} ${day.isToday ? 'today' : ''}`}
                                        >
                                            <span className="calendly-day-number">{day.day}</span>
                                            {day.isToday && <span className="calendly-today-dot" />}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Timezone Selector */}
                            <div className="calendly-timezone">
                                <span className="calendly-timezone-label">Time zone</span>
                                <div className="calendly-timezone-select">
                                    <Globe size={14} />
                                    <select
                                        value={timezone}
                                        onChange={(e) => handleTimezoneChange(e.target.value)}
                                    >
                                        {TIMEZONES.map(tz => (
                                            <option key={tz.value} value={tz.value}>
                                                {tz.label} ({getCurrentTimeDisplay()})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Time Slots Panel - slides in */}
                        <div className={`calendly-times-panel ${selectedDate ? 'visible' : ''}`}>
                            {selectedDate && (
                                <>
                                    <h3 className="calendly-times-title">
                                        {selectedDayInfo?.day_name}, {' '}
                                        {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                                    </h3>
                                    <div className="calendly-times-list">
                                        {slotsForSelectedDate.map((slot, idx) => {
                                            const isSelected = selectedSlots.some(s => s.start_time_utc === slot.start_time_utc);
                                            const isCurrentSelection = isDirectBooking && isSelected;
                                            return (
                                                <div key={idx} className="calendly-time-row">
                                                    <button
                                                        onClick={() => handleTimeSelect(slot)}
                                                        className={`calendly-time-btn ${isSelected ? 'selected' : ''}`}
                                                    >
                                                        {slot.start_time_local}
                                                        {!isDirectBooking && isSelected && (
                                                            <Check size={16} className="calendly-time-check" />
                                                        )}
                                                    </button>
                                                    {isCurrentSelection && (
                                                        <button
                                                            onClick={handleConfirmTime}
                                                            className="calendly-confirm-btn"
                                                        >
                                                            Confirm
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
